import React, { useMemo } from 'react';
import { useCenturionStore } from '../lib/store';
import { motion } from 'framer-motion';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line } from 'recharts';
import { Activity, TrendingUp, Target } from 'lucide-react';
import { getDaysBetween } from '../lib/game-logic';
const HABIT_LABELS: Record<string, string> = {
  noSugar: 'No Sugar',
  noAlcohol: 'No Alcohol',
  protein: 'Protein',
  water: 'Water',
  workout1: 'Workout 1',
  workout2: 'Workout 2',
  reading: 'Reading',
  skillWork: 'Skill Work'
};
export function AnalyticsDashboard() {
  const {
    profile,
    isLoaded,
    todayStr
  } = useCenturionStore();
  const stats = useMemo(() => {
    if (!profile) return null;
    const logs = Object.values(profile.logs).filter(log => log.finalized);
    const totalFinalizedDays = logs.length;
    // Habit Completion Rates
    const habitCounts: Record<string, number> = {
      noSugar: 0,
      noAlcohol: 0,
      protein: 0,
      water: 0,
      workout1: 0,
      workout2: 0,
      reading: 0,
      skillWork: 0
    };
    logs.forEach(log => {
      Object.values(log.habits).forEach(habit => {
        if (habit.completed) {
          habitCounts[habit.type] = (habitCounts[habit.type] || 0) + 1;
        }
      });
    });
    const completionRates = Object.entries(habitCounts).map(([key, count]) => ({
      name: HABIT_LABELS[key] || key,
      rate: totalFinalizedDays > 0 ? Math.round(count / totalFinalizedDays * 100) : 0,
      count
    }));
    // Sort by rate descending
    completionRates.sort((a, b) => b.rate - a.rate);
    // Last 14 Days Trend (Total habits completed per day)
    const sortedDates = Object.keys(profile.logs).sort();
    const last14Dates = sortedDates.slice(-14);
    const trendData = last14Dates.map(date => {
      const log = profile.logs[date];
      const completedCount = Object.values(log.habits).filter(h => h.completed).length;
      // Format date for display (e.g. "MM/DD")
      const d = new Date(date);
      const dateLabel = `${d.getMonth() + 1}/${d.getDate()}`;
      return {
        date: dateLabel,
        fullDate: date,
        completed: completedCount,
        isFinalized: log.finalized
      };
    });
    const currentDayNumber = getDaysBetween(profile.startDate, todayStr) + 1;
    return {
      totalFinalizedDays,
      completionRates,
      trendData,
      currentDayNumber
    };
  }, [profile, todayStr]);
  if (!isLoaded || !profile || !stats) {
    return <div className="flex items-center justify-center min-h-[60vh]" data-id="element-17">
        <div className="animate-pulse-slow text-centurion-500 font-mono-num text-xl tracking-widest" data-id="element-18">
          LOADING ANALYTICS...
        </div>
      </div>;
  }
  const CustomTooltip = ({
    active,
    payload,
    label
  }: any) => {
    if (active && payload && payload.length) {
      return <div className="bg-surface-400 border border-surface-100 p-3 rounded-lg shadow-xl" data-id="element-19">
          <p className="text-gray-200 font-medium mb-1" data-id="element-20">{label}</p>
          <p className="text-centurion-500 font-mono-num" data-id="element-21">
            {payload[0].value}
            {payload[0].name === 'rate' ? '%' : ' habits'}
          </p>
        </div>;
    }
    return null;
  };
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} className="max-w-4xl mx-auto pb-24" data-id="element-22">
      <header className="mb-8" data-id="element-23">
        <h1 className="text-3xl font-bold tracking-tight text-gray-100 mb-2" data-id="element-24">
          Analytics
        </h1>
        <p className="text-gray-400" data-id="element-25">
          Performance metrics and historical trends.
        </p>
      </header>

      {/* Top Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8" data-id="element-26">
        <div className="bg-surface-300 rounded-xl p-1 border border-surface-100 shadow-lg" data-id="element-27">
          <div className="bg-surface-400 rounded-lg p-5 h-full flex flex-col justify-between" data-id="element-28">
            <div className="flex items-center gap-2 text-gray-400 mb-2" data-id="element-29">
              <Activity size={18} data-id="element-30" />
              <h3 className="text-sm font-semibold uppercase tracking-wider" data-id="element-31">
                Current Streak
              </h3>
            </div>
            <div className="flex items-baseline gap-2" data-id="element-32">
              <span className="text-4xl font-mono-num font-bold text-centurion-500" data-id="element-33">
                {stats.currentDayNumber}
              </span>
              <span className="text-gray-500 text-sm" data-id="element-34">days</span>
            </div>
          </div>
        </div>

        <div className="bg-surface-300 rounded-xl p-1 border border-surface-100 shadow-lg" data-id="element-35">
          <div className="bg-surface-400 rounded-lg p-5 h-full flex flex-col justify-between" data-id="element-36">
            <div className="flex items-center gap-2 text-gray-400 mb-2" data-id="element-37">
              <Target size={18} data-id="element-38" />
              <h3 className="text-sm font-semibold uppercase tracking-wider" data-id="element-39">
                Perfect Days
              </h3>
            </div>
            <div className="flex items-baseline gap-2" data-id="element-40">
              <span className="text-4xl font-mono-num font-bold text-emerald-500" data-id="element-41">
                {stats.totalFinalizedDays}
              </span>
              <span className="text-gray-500 text-sm" data-id="element-42">total</span>
            </div>
          </div>
        </div>

        <div className="bg-surface-300 rounded-xl p-1 border border-surface-100 shadow-lg" data-id="element-43">
          <div className="bg-surface-400 rounded-lg p-5 h-full flex flex-col justify-between" data-id="element-44">
            <div className="flex items-center gap-2 text-gray-400 mb-2" data-id="element-45">
              <TrendingUp size={18} data-id="element-46" />
              <h3 className="text-sm font-semibold uppercase tracking-wider" data-id="element-47">
                Completion Avg
              </h3>
            </div>
            <div className="flex items-baseline gap-2" data-id="element-48">
              <span className="text-4xl font-mono-num font-bold text-blue-500" data-id="element-49">
                {stats.completionRates.length > 0 ? Math.round(stats.completionRates.reduce((acc, curr) => acc + curr.rate, 0) / stats.completionRates.length) : 0}
              </span>
              <span className="text-gray-500 text-sm" data-id="element-50">%</span>
            </div>
          </div>
        </div>
      </div>

      <div className="space-y-8" data-id="element-51">
        {/* Habit Completion Rates Chart */}
        <div className="bg-surface-300 rounded-xl p-1 border border-surface-100 shadow-xl" data-id="element-52">
          <div className="bg-surface-400 rounded-lg p-6" data-id="element-53">
            <h2 className="text-lg font-semibold text-gray-200 uppercase tracking-wider mb-6" data-id="element-54">
              Habit Consistency
            </h2>
            <div className="h-72 w-full" data-id="element-55">
              <ResponsiveContainer width="100%" height="100%" data-id="element-56">
                <BarChart data={stats.completionRates} layout="vertical" margin={{
                top: 5,
                right: 30,
                left: 40,
                bottom: 5
              }} data-id="element-57">
                  <CartesianGrid strokeDasharray="3 3" stroke="#242424" horizontal={true} vertical={false} data-id="element-58" />
                  <XAxis type="number" domain={[0, 100]} stroke="#555" tick={{
                  fill: '#888',
                  fontSize: 12
                }} data-id="element-59" />
                  <YAxis dataKey="name" type="category" stroke="#555" tick={{
                  fill: '#aaa',
                  fontSize: 12
                }} width={80} data-id="element-60" />
                  <Tooltip content={<CustomTooltip data-id="element-62" />} cursor={{
                  fill: '#2a2a2a'
                }} data-id="element-61" />
                  <Bar dataKey="rate" fill="#f59e0b" radius={[0, 4, 4, 0]} barSize={20} data-id="element-63" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* 14-Day Trend Chart */}
        <div className="bg-surface-300 rounded-xl p-1 border border-surface-100 shadow-xl" data-id="element-64">
          <div className="bg-surface-400 rounded-lg p-6" data-id="element-65">
            <h2 className="text-lg font-semibold text-gray-200 uppercase tracking-wider mb-6" data-id="element-66">
              14-Day Activity Trend
            </h2>
            <div className="h-64 w-full" data-id="element-67">
              <ResponsiveContainer width="100%" height="100%" data-id="element-68">
                <LineChart data={stats.trendData} margin={{
                top: 5,
                right: 20,
                left: -20,
                bottom: 5
              }} data-id="element-69">
                  <CartesianGrid strokeDasharray="3 3" stroke="#242424" vertical={false} data-id="element-70" />
                  <XAxis dataKey="date" stroke="#555" tick={{
                  fill: '#888',
                  fontSize: 12
                }} data-id="element-71" />
                  <YAxis domain={[0, 8]} stroke="#555" tick={{
                  fill: '#888',
                  fontSize: 12
                }} tickCount={5} data-id="element-72" />
                  <Tooltip content={<CustomTooltip data-id="element-74" />} data-id="element-73" />
                  <Line type="monotone" dataKey="completed" stroke="#10b981" strokeWidth={3} dot={{
                  fill: '#10b981',
                  r: 4,
                  strokeWidth: 2,
                  stroke: '#111'
                }} activeDot={{
                  r: 6,
                  fill: '#10b981',
                  stroke: '#fff'
                }} data-id="element-75" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </div>
    </motion.div>;
}